import React, {
  useEffect,
  useState
} from "react";

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import EventIcon from "@material-ui/icons/Event";
import MessageIcon from "@material-ui/icons/Message";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import {
  useHistory
} from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import formatSerializedId from "../../utils/formatSerializedId";

const formatCurrency = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const formatDateTime = value => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "-";
  }
};

const SectionTitle = ({
  icon,
  title,
  action
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{
      marginBottom: 13
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      style={{
        gap: 8
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          background: "#EFF6FF",
          color: "#2563EB"
        }}
      >
        {icon}
      </Box>

      <Typography
        style={{
          color: "#0F172A",
          fontWeight: 900,
          fontSize: 13
        }}
      >
        {title}
      </Typography>
    </Box>

    {action}
  </Box>
);

const Metric = ({
  value,
  label
}) => (
  <Box
    style={{
      padding: "13px 14px",
      borderRadius: 12,
      background: "#F8FAFC",
      border: "1px solid #EEF2F7",
      minWidth: 0
    }}
  >
    <Typography
      style={{
        color: "#0F172A",
        fontSize: 18,
        fontWeight: 950,
        letterSpacing: "-.03em"
      }}
    >
      {value}
    </Typography>

    <Typography
      style={{
        color: "#94A3B8",
        fontSize: 10,
        fontWeight: 700,
        marginTop: 3
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ContactOverviewDrawer = ({
  open,
  contactId,
  onClose,
  onStartConversation
}) => {
  const history = useHistory();

  const [loading, setLoading] =
    useState(false);

  const [data, setData] =
    useState(null);

  const [error, setError] =
    useState("");

  const [contactEditOpen, setContactEditOpen] =
    useState(false);

  const [customFieldsOpen, setCustomFieldsOpen] =
    useState(false);

  const [savingContact, setSavingContact] =
    useState(false);

  const [contactDraft, setContactDraft] =
    useState({
      name: "",
      number: "",
      email: ""
    });

  const [extraDraft, setExtraDraft] =
    useState([]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!open || !contactId) {
        return;
      }

      setLoading(true);
      setError("");
      setData(null);

      try {
        const response = await api.get(
          `/contacts/${contactId}/overview`
        );

        if (active) {
          setData(response.data);
        }
      } catch (err) {
        if (active) {
          setError(
            err?.response?.data?.error ||
            "Não foi possível carregar o cliente."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [
    open,
    contactId
  ]);

  const contact =
    data?.contact || null;

  const relationship =
    data?.relationship || {};

  const crm =
    data?.crm || {};

  const agenda =
    data?.agenda || {};

  const opportunity =
    crm.currentOpportunity || null;

  const nextAppointment =
    agenda.nextAppointment || null;

  useEffect(() => {
    if (!contact) return;

    setContactDraft({
      name: contact.name || "",
      number: contact.number || "",
      email: contact.email || ""
    });

    setExtraDraft(
      Array.isArray(contact.extraInfo)
        ? contact.extraInfo.map(info => ({
            id: info.id,
            name: info.name || "",
            value: info.value || ""
          }))
        : []
    );
  }, [
    contact?.id,
    contact?.updatedAt
  ]);

  const openConversation = () => {
    if (
      relationship?.lastTicket?.uuid
    ) {
      onClose();

      history.push(
        `/tickets/${relationship.lastTicket.uuid}`
      );

      return;
    }

    if (
      contact &&
      onStartConversation
    ) {
      onClose();
      onStartConversation(contact);
    }
  };

  const openCRM = () => {
    if (!contact?.id) return;

    onClose();

    history.push(
      `/crm?contactId=${contact.id}`
    );
  };

  const refreshOverview = async () => {
    if (!contactId) return;

    const response = await api.get(
      `/contacts/${contactId}/overview`
    );

    setData(response.data);
  };


  const normalizeExtraInfo = list =>
    (Array.isArray(list) ? list : [])
      .map(info => ({
        ...(info.id ? { id: info.id } : {}),
        name: String(info.name || "").trim(),
        value: String(info.value || "").trim()
      }))
      .filter(
        info => info.name || info.value
      );


  const baseContactPayload = () => ({
    name: contact?.name || "",
    number: String(
      contact?.number || ""
    ).replace(/\D/g, ""),
    email: contact?.email || "",
    extraInfo: normalizeExtraInfo(
      contact?.extraInfo
    )
  });


  const openContactEditor = () => {
    if (!contact) return;

    setContactDraft({
      name: contact.name || "",
      number: contact.number || "",
      email: contact.email || ""
    });

    setContactEditOpen(true);
  };


  const openCustomFieldsEditor = () => {
    const current =
      Array.isArray(contact?.extraInfo)
        ? contact.extraInfo.map(info => ({
            id: info.id,
            name: info.name || "",
            value: info.value || ""
          }))
        : [];

    setExtraDraft(
      current.length
        ? current
        : [{
            name: "",
            value: ""
          }]
    );

    setCustomFieldsOpen(true);
  };


  const handleSaveContact = async () => {
    if (!contact?.id) return;

    const name = String(
      contactDraft.name || ""
    ).trim();

    const number = String(
      contactDraft.number || ""
    ).replace(/\D/g, "");

    const email = String(
      contactDraft.email || ""
    ).trim();

    if (!name) {
      toast.error("Informe o nome do contato.");
      return;
    }

    if (!number) {
      toast.error("Informe o telefone do contato.");
      return;
    }

    setSavingContact(true);

    try {
      await api.put(
        `/contacts/${contact.id}`,
        {
          ...baseContactPayload(),
          name,
          number,
          email
        }
      );

      await refreshOverview();

      setContactEditOpen(false);

      toast.success(
        "Dados do contato atualizados."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSavingContact(false);
    }
  };


  const handleAddExtraField = () => {
    setExtraDraft(current => [
      ...current,
      {
        name: "",
        value: ""
      }
    ]);
  };


  const handleChangeExtraField = (
    index,
    key,
    value
  ) => {
    setExtraDraft(current =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [key]: value
            }
          : item
      )
    );
  };


  const handleRemoveExtraField = index => {
    setExtraDraft(current =>
      current.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );
  };


  const handleSaveExtraFields = async () => {
    if (!contact?.id) return;

    const normalized =
      normalizeExtraInfo(extraDraft);

    setSavingContact(true);

    try {
      await api.put(
        `/contacts/${contact.id}`,
        {
          ...baseContactPayload(),
          extraInfo: normalized
        }
      );

      await refreshOverview();

      setExtraDraft(normalized);
      setCustomFieldsOpen(false);

      toast.success(
        "Informações do cliente atualizadas."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSavingContact(false);
    }
  };


  const openAgenda = () => {
    onClose();
    history.push("/agenda");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          width: 560,
          maxWidth: "100vw",
          background: "#F8FAFC"
        }
      }}
    >
      <Box
        style={{
          minHeight: "100%",
          boxSizing: "border-box"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{
            padding: "17px 19px",
            background: "#FFFFFF",
            borderBottom: "1px solid #E5EAF1",
            position: "sticky",
            top: 0,
            zIndex: 10
          }}
        >
          <Box>
            <Typography
              style={{
                color: "#0F172A",
                fontSize: 14,
                fontWeight: 950
              }}
            >
              Cliente 360
            </Typography>

            <Typography
              style={{
                color: "#94A3B8",
                fontSize: 10,
                marginTop: 2
              }}
            >
              Relacionamento, oportunidades e próximos passos
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              minHeight: 420
            }}
          >
            <CircularProgress
              size={28}
            />
          </Box>
        )}

        {!loading && error && (
          <Box
            style={{
              margin: 20,
              padding: 16,
              borderRadius: 13,
              background: "#FEF2F2",
              border: "1px solid #FECACA"
            }}
          >
            <Typography
              style={{
                color: "#B91C1C",
                fontSize: 12,
                fontWeight: 700
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {!loading &&
          !error &&
          contact && (
          <>
            {/* PERFIL */}
            <Box
              style={{
                padding: "24px 22px 20px",
                background:
                  "linear-gradient(135deg, #071426 0%, #0D2D63 58%, #155EEF 100%)",
                color: "#FFFFFF"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: 14
                }}
              >
                <Avatar
                  src={
                    contact.urlPicture ||
                    contact.profilePicUrl
                  }
                  style={{
                    width: 58,
                    height: 58,
                    fontSize: 21,
                    fontWeight: 900,
                    border:
                      "3px solid rgba(255,255,255,.18)"
                  }}
                >
                  {String(
                    contact.name || "C"
                  ).slice(0, 1)}
                </Avatar>

                <Box
                  style={{
                    minWidth: 0,
                    flex: 1
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{
                      gap: 8
                    }}
                  >
                    <Typography
                      style={{
                        color: "#FFFFFF",
                        fontSize: 19,
                        fontWeight: 950,
                        letterSpacing: "-.025em"
                      }}
                    >
                      {contact.name || "Cliente"}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        contact.active
                          ? "Ativo"
                          : "Bloqueado"
                      }
                      style={{
                        height: 21,
                        fontSize: 9,
                        fontWeight: 900,
                        color:
                          contact.active
                            ? "#166534"
                            : "#991B1B",
                        background:
                          contact.active
                            ? "#DCFCE7"
                            : "#FEE2E2"
                      }}
                    />
                  </Box>

                  <Typography
                    style={{
                      marginTop: 5,
                      color:
                        "rgba(219,234,254,.84)",
                      fontSize: 11
                    }}
                  >
                    {contact.isGroup
                      ? contact.number
                      : (
                          formatSerializedId(
                            contact.number
                          ) ||
                          contact.number
                        )}
                  </Typography>

                  {contact.email && (
                    <Typography
                      style={{
                        marginTop: 2,
                        color:
                          "rgba(219,234,254,.65)",
                        fontSize: 10
                      }}
                    >
                      {contact.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                display="flex"
                flexWrap="wrap"
                style={{
                  gap: 8,
                  marginTop: 19
                }}
              >
                <Button
                  size="small"
                  startIcon={
                    <WhatsAppIcon />
                  }
                  onClick={
                    openConversation
                  }
                  style={{
                    background: "#FFFFFF",
                    color: "#155EEF",
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: 9,
                    padding:
                      "7px 12px"
                  }}
                >
                  Conversar
                </Button>

                <Button
                  size="small"
                  startIcon={
                    <EditIcon />
                  }
                  onClick={
                    openContactEditor
                  }
                  style={{
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontWeight: 850,
                    borderRadius: 9,
                    padding:
                      "7px 12px",
                    border:
                      "1px solid rgba(255,255,255,.22)"
                  }}
                >
                  Editar
                </Button>

                <Button
                  size="small"
                  startIcon={
                    <EventIcon />
                  }
                  onClick={
                    openAgenda
                  }
                  style={{
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontWeight: 850,
                    borderRadius: 9,
                    padding:
                      "7px 12px",
                    border:
                      "1px solid rgba(255,255,255,.22)"
                  }}
                >
                  Agenda
                </Button>
              </Box>
            </Box>

            <Box
              style={{
                padding: 20
              }}
            >
              {/* MÉTRICAS */}
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                  marginBottom: 20
                }}
              >
                <Metric
                  value={
                    relationship.ticketsCount ||
                    0
                  }
                  label="Atendimentos"
                />

                <Metric
                  value={
                    relationship.messagesCount ||
                    0
                  }
                  label="Mensagens"
                />

                <Metric
                  value={
                    crm.total || 0
                  }
                  label="Oportunidades"
                />

                <Metric
                  value={
                    opportunity
                      ? formatCurrency(opportunity.value)
                      : "R$ 0,00"
                  }
                  label="Em negociação"
                />
              </Box>

              {/* CRM */}
              <Box
                style={{
                  padding: 17,
                  marginBottom: 13,
                  borderRadius: 16,
                  border:
                    "1px solid #E5EAF1",
                  background: "#FFFFFF"
                }}
              >
                <SectionTitle
                  title="Oportunidades"
                  icon={
                    <TrendingUpIcon
                      style={{
                        fontSize: 17
                      }}
                    />
                  }
                  action={
                    <Button
                      size="small"
                      onClick={openCRM}
                      style={{
                        minWidth: 0,
                        padding:
                          "4px 7px",
                        color: "#2563EB",
                        fontSize: 9.5,
                        fontWeight: 900,
                        textTransform:
                          "none"
                      }}
                    >
                      Abrir CRM →
                    </Button>
                  }
                />

                {opportunity ? (
                  <Box
                    style={{
                      padding: 14,
                      borderRadius: 14,
                      background: "#F8FAFC",
                      border: "1px solid #E5EAF1"
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{
                        gap: 10
                      }}
                    >
                      <Box
                        style={{
                          minWidth: 0
                        }}
                      >
                        <Typography
                          style={{
                            color:
                              "#0F172A",
                            fontSize: 13,
                            fontWeight: 900
                          }}
                        >
                          {opportunity.title ||
                            opportunity.product ||
                            "Oportunidade"}
                        </Typography>

                        <Box
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          style={{
                            gap: 7,
                            marginTop: 7
                          }}
                        >
                          <Box
                            style={{
                              padding:
                                "5px 8px",
                              borderRadius:
                                8,
                              background:
                                opportunity.stageColor
                                  ? `${opportunity.stageColor}18`
                                  : "#EFF6FF",
                              color:
                                opportunity.stageColor ||
                                "#2563EB",
                              fontSize:
                                9.5,
                              fontWeight:
                                900
                            }}
                          >
                            {opportunity.stageName ||
                              "Sem etapa"}
                          </Box>

                          <Typography
                            style={{
                              color:
                                "#64748B",
                              fontSize:
                                10
                            }}
                          >
                            {Number(
                              opportunity.probability ||
                                0
                            )}
                            % probabilidade
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        style={{
                          color:
                            "#0F172A",
                          fontSize: 15,
                          fontWeight: 950,
                          whiteSpace:
                            "nowrap"
                        }}
                      >
                        {formatCurrency(
                          opportunity.value
                        )}
                      </Typography>
                    </Box>

                    {crm.open > 1 && (
                      <Typography
                        style={{
                          marginTop: 11,
                          color:
                            "#94A3B8",
                          fontSize: 9.5
                        }}
                      >
                        + {crm.open - 1} oportunidade
                        {crm.open - 1 === 1
                          ? ""
                          : "s"}{" "}
                        aberta
                        {crm.open - 1 === 1
                          ? ""
                          : "s"}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    style={{
                      color: "#94A3B8",
                      fontSize: 11
                    }}
                  >
                    Nenhuma oportunidade registrada para este cliente.
                  </Typography>
                )}
              </Box>

              {/* RELACIONAMENTO */}
              <Box
                style={{
                  padding: 17,
                  marginBottom: 13,
                  borderRadius: 16,
                  border: "1px solid #E5EAF1",
                  background: "#FFFFFF"
                }}
              >
                <SectionTitle
                  title="Relacionamento"
                  icon={
                    <MessageIcon
                      style={{
                        fontSize: 17
                      }}
                    />
                  }
                  action={
                    relationship?.lastTicket?.uuid ? (
                      <Button
                        size="small"
                        onClick={openConversation}
                        style={{
                          minWidth: 0,
                          padding: "4px 7px",
                          color: "#2563EB",
                          fontSize: 9.5,
                          fontWeight: 900,
                          textTransform: "none"
                        }}
                      >
                        Abrir conversa →
                      </Button>
                    ) : null
                  }
                />

                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
                    gap: 8
                  }}
                >
                  <Box
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#F8FAFC"
                    }}
                  >
                    <Typography
                      style={{
                        color: "#94A3B8",
                        fontSize: 8.5,
                        fontWeight: 850,
                        textTransform: "uppercase"
                      }}
                    >
                      Última interação
                    </Typography>

                    <Typography
                      style={{
                        color: "#334155",
                        fontSize: 10.5,
                        fontWeight: 800,
                        marginTop: 4
                      }}
                    >
                      {relationship?.lastTicket?.updatedAt
                        ? formatDateTime(relationship.lastTicket.updatedAt)
                        : relationship?.lastTicket?.createdAt
                        ? formatDateTime(relationship.lastTicket.createdAt)
                        : "Sem histórico"}
                    </Typography>
                  </Box>

                  <Box
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#F8FAFC"
                    }}
                  >
                    <Typography
                      style={{
                        color: "#94A3B8",
                        fontSize: 8.5,
                        fontWeight: 850,
                        textTransform: "uppercase"
                      }}
                    >
                      Canal
                    </Typography>

                    <Typography
                      style={{
                        color: "#334155",
                        fontSize: 10.5,
                        fontWeight: 800,
                        marginTop: 4
                      }}
                    >
                      WhatsApp
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  style={{
                    color: "#94A3B8",
                    fontSize: 9.5,
                    marginTop: 11
                  }}
                >
                  {relationship.ticketsCount || 0} atendimento(s) •{" "}
                  {relationship.messagesCount || 0} mensagem(ns)
                </Typography>
              </Box>

              {/* AGENDA */}
              <Box
                style={{
                  padding: 17,
                  marginBottom: 13,
                  borderRadius: 16,
                  border:
                    "1px solid #E5EAF1",
                  background: "#FFFFFF"
                }}
              >
                <SectionTitle
                  title="Próximo contato"
                  icon={
                    <EventIcon
                      style={{
                        fontSize: 17
                      }}
                    />
                  }
                  action={
                    <Button
                      size="small"
                      onClick={
                        openAgenda
                      }
                      style={{
                        minWidth: 0,
                        padding:
                          "4px 7px",
                        color:
                          "#7C3AED",
                        fontSize:
                          9.5,
                        fontWeight:
                          900,
                        textTransform:
                          "none"
                      }}
                    >
                      Abrir agenda →
                    </Button>
                  }
                />

                {nextAppointment ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{
                      gap: 12
                    }}
                  >
                    <Box>
                      <Typography
                        style={{
                          color:
                            "#0F172A",
                          fontSize:
                            12,
                          fontWeight:
                            900
                        }}
                      >
                        {nextAppointment.serviceName ||
                          "Agendamento"}
                      </Typography>

                      <Typography
                        style={{
                          color:
                            "#64748B",
                          fontSize:
                            10,
                          marginTop:
                            4
                        }}
                      >
                        {formatDateTime(
                          nextAppointment.startTime
                        )}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={
                        nextAppointment.status ||
                        "agendado"
                      }
                      style={{
                        fontWeight:
                          850,
                        fontSize: 9,
                        background:
                          "#F5F3FF",
                        color:
                          "#7C3AED"
                      }}
                    />
                  </Box>
                ) : (
                  <Typography
                    style={{
                      color:
                        "#94A3B8",
                      fontSize: 11
                    }}
                  >
                    Nenhum próximo agendamento.
                  </Typography>
                )}
              </Box>

              {/* RELACIONAMENTO */}
              <Box
                style={{
                  padding: 17,
                  marginBottom: 13,
                  borderRadius: 16,
                  border:
                    "1px solid #E5EAF1",
                  background: "#FFFFFF"
                }}
              >
                <SectionTitle
                  title="Relacionamento"
                  icon={
                    <MessageIcon
                      style={{
                        fontSize: 17
                      }}
                    />
                  }
                />

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    gap: 12
                  }}
                >
                  <Box>
                    <Typography
                      style={{
                        color:
                          "#64748B",
                        fontSize: 10
                      }}
                    >
                      Última interação
                    </Typography>

                    <Typography
                      style={{
                        color:
                          "#0F172A",
                        fontSize: 11,
                        fontWeight: 850,
                        marginTop: 3
                      }}
                    >
                      {formatDateTime(
                        relationship.lastInteractionAt
                      )}
                    </Typography>
                  </Box>

                  {relationship.lastTicket?.status && (
                    <Chip
                      size="small"
                      label={
                        relationship.lastTicket.status
                      }
                      style={{
                        fontSize: 9,
                        fontWeight:
                          800
                      }}
                    />
                  )}
                </Box>

                {relationship.lastMessage?.body && (
                  <>
                    <Divider
                      style={{
                        margin:
                          "14px 0"
                      }}
                    />

                    <Typography
                      style={{
                        color:
                          "#94A3B8",
                        fontSize: 9.5,
                        fontWeight:
                          750,
                        textTransform:
                          "uppercase",
                        letterSpacing:
                          ".05em"
                      }}
                    >
                      Última mensagem
                    </Typography>

                    <Typography
                      style={{
                        color:
                          "#475569",
                        fontSize: 11,
                        lineHeight: 1.55,
                        marginTop: 7
                      }}
                    >
                      “
                      {String(
                        relationship.lastMessage.body
                      ).slice(
                        0,
                        220
                      )}
                      ”
                    </Typography>
                  </>
                )}
              </Box>

              {/* INFORMAÇÕES */}
              <Box
                style={{
                  padding: 17,
                  borderRadius: 16,
                  border:
                    "1px solid #E5EAF1",
                  background: "#FFFFFF"
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    gap: 10,
                    marginBottom: 13
                  }}
                >
                  <Typography
                    style={{
                      color: "#0F172A",
                      fontSize: 13,
                      fontWeight: 900
                    }}
                  >
                    Informações do cliente
                  </Typography>

                  <Button
                    size="small"
                    onClick={
                      openCustomFieldsEditor
                    }
                    style={{
                      minWidth: 0,
                      padding: "5px 9px",
                      borderRadius: 8,
                      textTransform: "none",
                      color: "#155EEF",
                      background: "#EFF6FF",
                      fontSize: 9.5,
                      fontWeight: 900
                    }}
                  >
                    + Informação
                  </Button>
                </Box>

                <Box
                  style={{
                    display: "grid",
                    gap: 10
                  }}
                >
                  <Box>
                    <Typography
                      style={{
                        color: "#94A3B8",
                        fontSize: 9.5
                      }}
                    >
                      Canal
                    </Typography>

                    <Typography
                      style={{
                        color: "#334155",
                        fontSize: 11,
                        fontWeight: 800,
                        marginTop: 2,
                        textTransform:
                          "capitalize"
                      }}
                    >
                      {contact.channel || "-"}
                    </Typography>
                  </Box>

                  {contact.tags?.length > 0 && (
                    <Box>
                      <Typography
                        style={{
                          color: "#94A3B8",
                          fontSize: 9.5,
                          marginBottom: 6
                        }}
                      >
                        Tags
                      </Typography>

                      <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 5 }}
                      >
                        {contact.tags.map(
                          tag => (
                            <Chip
                              key={tag.id}
                              size="small"
                              label={tag.name}
                              style={{
                                height: 22,
                                fontSize: 9
                              }}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  )}

                  {contact.extraInfo?.length > 0 ? (
                    <Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{
                          marginBottom: 7
                        }}
                      >
                        <Typography
                          style={{
                            color: "#94A3B8",
                            fontSize: 9.5
                          }}
                        >
                          Campos personalizados
                        </Typography>

                        <Button
                          size="small"
                          onClick={
                            openCustomFieldsEditor
                          }
                          style={{
                            minWidth: 0,
                            padding: 0,
                            textTransform:
                              "none",
                            color: "#155EEF",
                            fontSize: 9,
                            fontWeight: 850
                          }}
                        >
                          Editar
                        </Button>
                      </Box>

                      {contact.extraInfo.map(
                        item => (
                          <Box
                            key={item.id}
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            style={{
                              gap: 12,
                              padding: "9px 0",
                              borderBottom:
                                "1px solid #F1F5F9"
                            }}
                          >
                            <Typography
                              style={{
                                color: "#64748B",
                                fontSize: 10
                              }}
                            >
                              {item.name}
                            </Typography>

                            <Typography
                              style={{
                                color: "#0F172A",
                                fontSize: 10,
                                fontWeight: 800,
                                textAlign: "right",
                                maxWidth: "58%",
                                wordBreak:
                                  "break-word"
                              }}
                            >
                              {item.value || "-"}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  ) : (
                    <Box
                      onClick={
                        openCustomFieldsEditor
                      }
                      style={{
                        padding: "11px 12px",
                        borderRadius: 10,
                        border:
                          "1px dashed #CBD5E1",
                        background: "#F8FAFC",
                        cursor: "pointer"
                      }}
                    >
                      <Typography
                        style={{
                          color: "#64748B",
                          fontSize: 10,
                          fontWeight: 750
                        }}
                      >
                        Nenhuma informação personalizada.
                      </Typography>

                      <Typography
                        style={{
                          color: "#155EEF",
                          fontSize: 9.5,
                          fontWeight: 800,
                          marginTop: 3
                        }}
                      >
                        + Adicionar informação
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Dialog
        open={contactEditOpen}
        onClose={() => {
          if (!savingContact) {
            setContactEditOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          style={{
            padding: "18px 20px 12px"
          }}
        >
          <Typography
            style={{
              color: "#0F172A",
              fontSize: 15,
              fontWeight: 900
            }}
          >
            Editar contato
          </Typography>

          <Typography
            style={{
              color: "#94A3B8",
              fontSize: 10.5,
              marginTop: 3
            }}
          >
            Atualize os dados sem sair do Cliente 360.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            label="Nome"
            value={contactDraft.name}
            onChange={event =>
              setContactDraft(current => ({
                ...current,
                name: event.target.value
              }))
            }
          />

          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            label="Telefone"
            value={contactDraft.number}
            onChange={event =>
              setContactDraft(current => ({
                ...current,
                number: event.target.value
              }))
            }
          />

          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            label="E-mail"
            value={contactDraft.email}
            onChange={event =>
              setContactDraft(current => ({
                ...current,
                email: event.target.value
              }))
            }
          />
        </DialogContent>

        <DialogActions
          style={{
            padding: "12px 20px 18px"
          }}
        >
          <Button
            onClick={() =>
              setContactEditOpen(false)
            }
            disabled={savingContact}
            style={{
              textTransform: "none",
              fontWeight: 800,
              color: "#64748B"
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveContact}
            disabled={savingContact}
            style={{
              textTransform: "none",
              fontWeight: 850,
              borderRadius: 9,
              boxShadow: "none"
            }}
          >
            {savingContact
              ? "Salvando..."
              : "Salvar alterações"}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={customFieldsOpen}
        onClose={() => {
          if (!savingContact) {
            setCustomFieldsOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          style={{
            padding: "18px 20px 12px"
          }}
        >
          <Typography
            style={{
              color: "#0F172A",
              fontSize: 15,
              fontWeight: 900
            }}
          >
            Informações personalizadas
          </Typography>

          <Typography
            style={{
              color: "#94A3B8",
              fontSize: 10.5,
              marginTop: 3
            }}
          >
            Adicione os dados que sua operação precisa acompanhar neste cliente.
          </Typography>
        </DialogTitle>

        <DialogContent>
          {extraDraft.map(
            (info, index) => (
              <Box
                key={
                  info.id ||
                  `new-${index}`
                }
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(0,.8fr) minmax(0,1.2fr) auto",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 9
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  label="Campo"
                  placeholder="Ex.: Seguradora atual"
                  value={info.name || ""}
                  onChange={event =>
                    handleChangeExtraField(
                      index,
                      "name",
                      event.target.value
                    )
                  }
                />

                <TextField
                  variant="outlined"
                  size="small"
                  label="Informação"
                  placeholder="Ex.: Porto Seguro"
                  value={info.value || ""}
                  onChange={event =>
                    handleChangeExtraField(
                      index,
                      "value",
                      event.target.value
                    )
                  }
                />

                <Button
                  size="small"
                  onClick={() =>
                    handleRemoveExtraField(
                      index
                    )
                  }
                  disabled={savingContact}
                  style={{
                    minWidth: 0,
                    color: "#D92D20",
                    textTransform: "none",
                    fontSize: 9.5,
                    fontWeight: 850
                  }}
                >
                  Remover
                </Button>
              </Box>
            )
          )}

          {extraDraft.length === 0 ? (
            <Box
              style={{
                padding: "18px 12px",
                marginBottom: 9,
                textAlign: "center",
                borderRadius: 12,
                background: "#F8FAFC",
                border:
                  "1px solid #EEF2F7"
              }}
            >
              <Typography
                style={{
                  color: "#64748B",
                  fontSize: 10.5,
                  fontWeight: 800
                }}
              >
                Nenhuma informação cadastrada.
              </Typography>
            </Box>
          ) : null}

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={handleAddExtraField}
            disabled={savingContact}
            style={{
              marginTop: 5,
              height: 38,
              borderRadius: 10,
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 850
            }}
          >
            + Adicionar outro campo
          </Button>
        </DialogContent>

        <DialogActions
          style={{
            padding: "12px 20px 18px"
          }}
        >
          <Button
            onClick={() =>
              setCustomFieldsOpen(false)
            }
            disabled={savingContact}
            style={{
              textTransform: "none",
              fontWeight: 800,
              color: "#64748B"
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveExtraFields}
            disabled={savingContact}
            style={{
              textTransform: "none",
              fontWeight: 850,
              borderRadius: 9,
              boxShadow: "none"
            }}
          >
            {savingContact
              ? "Salvando..."
              : "Salvar informações"}
          </Button>
        </DialogActions>
      </Dialog>


    </Drawer>
  );
};

export default ContactOverviewDrawer;
